import { ArrowRight, Check, Clock3, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { wave } from '@round/api';
import type { CampaignStatus } from '@/types/creator';
import { getInviteOffer, getTimeLeft } from '@/utility/campaignDisplay';
import StatusPill from '@/creator/components/StatusPill';
import BudgetUsedMeter from '@/creator/components/BudgetUsedMeter';
import type { OfferSummary } from '@/utility/contracts';
import CampaignArtwork from './CampaignArtwork';

type Props = {
    invite: wave.CreatorInvite;
    offerCount: number;
    /** The invite's one fixed-fee or bundle contract, described from its own terms. */
    offerSummary?: OfferSummary | null;
    status: CampaignStatus;
    budgetUsedPercentage: number | null;
    onOpenCampaign: (inviteId: string) => void;
};

const CampaignCard = ({ invite, offerCount, offerSummary, status, budgetUsedPercentage, onOpenCampaign }: Props) => {
    const { amount: offerAmount, detail: offerDetail } = getInviteOffer(invite);
    const timeLabel = status === 'closed' ? 'Closed' : getTimeLeft(invite.deadline);
    const TimelineIcon: LucideIcon = status === 'completed' ? Check : status === 'declined' ? X : Clock3;

    return (
        <article className="campaign-card">
            <CampaignArtwork
                accent="sky"
                size="featured"
                src={invite.artwork_url}
                alt={`${invite.release_name} artwork`}
            />
            <div className="campaign-card__status">
                <StatusPill status={status} />
            </div>
            <div className="campaign-card__content">
                <div className="campaign-card__top">
                    <h2>{invite.release_name}</h2>
                    {offerCount > 1 ? (
                        <div className="campaign-card__offer campaign-card__offer--multiple">
                            <strong>Multiple offers</strong>
                            <small>{`${offerCount} offers`}</small>
                        </div>
                    ) : offerSummary ? (
                        <div className="campaign-card__offer">
                            <strong>
                                {offerSummary.isMaximum ? `Up to ${offerSummary.amount}` : offerSummary.amount}
                            </strong>
                            <small>{offerSummary.detail}</small>
                        </div>
                    ) : (
                        <div className="campaign-card__offer">
                            <strong>
                                {invite.campaign_type === 'cpm' && invite.max_earnable !== null
                                    ? `Up to ${offerAmount}`
                                    : offerAmount}
                            </strong>
                            {offerDetail ? <small>{offerDetail}</small> : null}
                        </div>
                    )}
                </div>
                <div className="campaign-card__meta">
                    {offerSummary?.isWithdrawn ? null : (
                        <span>
                            <TimelineIcon size={14} /> <span>{timeLabel}</span>
                        </span>
                    )}
                    <span className="campaign-card__accounts">{invite.artist_name}</span>
                </div>
                {status === 'invited' || status === 'active' ? (
                    <BudgetUsedMeter percentage={budgetUsedPercentage} isCompact />
                ) : null}
                <div className="campaign-card__footer">
                    <button
                        className="button button--primary campaign-card__action"
                        type="button"
                        onClick={() => onOpenCampaign(invite.id)}
                    >
                        <span>View campaign</span> <ArrowRight size={15} />
                    </button>
                </div>
            </div>
        </article>
    );
};

export default CampaignCard;
